import * as THREE from "three";
import { OutlineEffect } from "./OutlineEffect";
import Experience from "./Experience.js";
import { CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer";
import debug from "debug";

const log = debug("app:renderer");

/**
 * @category Experience
 */
export default class Renderer extends THREE.WebGLRenderer {
  /**
   * Creates an instance of the Renderer class.
   * @param {Object} myObject - The object containing viewer properties.
   */
  constructor(myObject) {
    super();
    this.experience = new Experience();
    this.eventHub = this.experience.eventHub;
    this.canvas = this.experience.canvas;
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.camera = this.experience.camera.instance;

    this.setInstance(myObject);
    this.setCSSRenderer();

    this.renderLoopActive = false;
    this.needsRender = false;
    this.stopRenderTimeout = null;

    this.eventHub.on("resize", (newSize) => {
      this.resize(newSize);
      this.requestRender();
    });

    // Listen for needsRender event
    this.eventHub.on("needsRender", (state) => {
      if (state === "true") {
        this.requestRender();
        this.eventHub.emit("needsRender", "false");
        log("event recieved, emmited false");
      }
    });

    // Scene and model updates (beyond camera) also require a render
    this.eventHub.on("loading-finished", () => this.requestRender());
    this.eventHub.on("deleteScene", () => this.requestRender());
  }

  /**
   * Requests a render and ensures the loop continues if necessary.
   */
  requestRender() {
    this.needsRender = true;

    // Reset any pending stop timeout (prevents unnecessary stopping)
    if (this.stopRenderTimeout) {
      clearTimeout(this.stopRenderTimeout);
      this.stopRenderTimeout = null;
    }

    if (!this.renderLoopActive) {
      this.startRenderLoop();
    }

    // ✅ Schedule render stopping after 3 seconds of inactivity
    this.stopRenderTimeout = setTimeout(() => {
      this.needsRender = false;
      this.renderLoopActive = false;
      log("loop stopped");
    }, 3000);
  }

  /**
   * Starts the render loop if not already active.
   */
  startRenderLoop() {
    log("looping");
    if (this.renderLoopActive) return;
    this.renderLoopActive = true;

    const loop = () => {
      if (!this.needsRender) {
        this.renderLoopActive = false;
        return;
      }
      requestAnimationFrame(loop);
      this.update();
    };

    requestAnimationFrame(loop);
  }

  cleanup() {
    try {
      console.log("Cleaning up renderer...");

      // Remove event listeners
      this.eventHub.off("resize", this.resize);

      // Dispose WebGL renderer
      if (this.instance) {
        this.instance.dispose();
      }

      // Remove CSS renderer
      if (this.cssRenderer && this.cssRenderer.domElement) {
        this.cssRenderer.domElement.remove();
        this.cssRenderer = null;
      }

      // Dispose OutlineEffect
      if (this.effect && typeof this.effect.cleanup === "function") {
        this.effect.cleanup();
      }

      // Dispose scene objects
      this.scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });

      console.log("Renderer cleanup completed.");
    } catch (error) {
      console.error("Error during renderer cleanup:", error);
    }
  }

  /**
   * Initializes the WebGLRenderer instance and applies settings.
   * @param {Object} myObject - The object containing viewer properties.
   */
  setInstance(myObject) {
    this.myObject = myObject;
    this.instance = new THREE.WebGLRenderer({
      antialias: true,
      canvas: this.canvas,
    });
    this.instance.physicallyCorrectLights =
      this.myObject.viewer.renderer.physicallyCorrectLights;
    this.instance.outputColorSpace = THREE.SRGBColorSpace;
    this.instance.setClearColor(new THREE.Color("#ffffff"), 1);
    this.instance.shadowMap.enabled = this.myObject.viewer.renderer.shadowMap;
    this.instance.shadowMap.type = THREE.PCFSoftShadowMap;
    this.instance.setSize(this.sizes.reSize.width, this.sizes.reSize.height);
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2));
    this.effect = new OutlineEffect(this.instance);
  }

  /**
   * Initializes and configures the CSS3DRenderer.
   */
  setCSSRenderer() {
    this.cssRenderer = new CSS3DRenderer();
    this.cssRenderer.setSize(this.sizes.reSize.width, this.sizes.reSize.height);
    this.cssRenderer.domElement.style.position = "absolute";
    this.cssRenderer.domElement.style.top = "0px";
    this.cssRenderer.domElement.style.pointerEvents = "none"; // Disable pointer events to allow interaction with the scene
    document.body.appendChild(this.cssRenderer.domElement);
  }

  /**
   * Resizes the renderer and CSS3DRenderer based on new size.
   * @param {Object} newSize - The new width and height for resizing.
   * @param {number} newSize.width - The new width of the renderer.
   * @param {number} newSize.height - The new height of the renderer.
   */
  resize(newSize) {
    this.instance.setSize(newSize.width, newSize.height);
    this.instance.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    if (this.cssRenderer) {
      this.cssRenderer.setSize(newSize.width, newSize.height);
    }
  }

  /**
   * Updates the renderer and CSS3DRenderer to render the scene and camera.
   * Skips post-processing while camera is moving for better interaction performance;
   * post-processing runs when the camera is idle.
   */
  update() {
    if (this.instance && this.camera) {
      const camera = this.experience.camera;
      const postProc = this.experience.postProcessing;
      const usePostProcessing =
        postProc?.enabled && !camera?.isManipulating;

      if (usePostProcessing) {
        postProc.render();
      } else {
        this.instance.render(this.scene, this.camera);
      }

      if (this.cssRenderer) {
        this.cssRenderer.render(this.scene, this.camera);
      }
    }
  }
}
